import { useAccountContext } from '@seek/hirer-graphql-react';
import {
  Step,
  Stepper,
  type TeamMemberForJobAccess,
  useSaveHirerTeamMemberJobAccessAndNotify,
  HirerTeamMemberJobAccessForm,
  type StepActionResult,
  type OnNotifyChangeEventData,
} from '@seek/hirer-team-member-job-access';
import { useTranslations } from '@vocab/react';
import { useState, type FC, type PropsWithChildren } from 'react';

import translations from './.vocab';
import { AddTeamMembersCallout } from './AddTeamMembersCallout';
import { TeamMemberEmail } from './TeamMemberEmail';

interface Props {
  onComplete(): void;
  onCancel(): void;
  teamMembers: TeamMemberForJobAccess[];
  jobTitle?: string;
  jobId: string;
}

interface TeamMemberAccessStepperState {
  stepNames: string[];
  notifyTeam: boolean;
}

export const TeamMemberAccessStepper: FC<PropsWithChildren<Props>> = ({
  onComplete,
  onCancel,
  teamMembers,
  jobTitle,
  jobId,
}: Props) => {
  const saveHirerTeamMemberJobAccessAndNotify =
    useSaveHirerTeamMemberJobAccessAndNotify();
  const { t } = useTranslations(translations);
  const selectTeamStep = t('Select team');
  const emailTeamStep = t('Email team');
  const [state, setState] = useState<TeamMemberAccessStepperState>({
    stepNames: [selectTeamStep],
    notifyTeam: false,
  });
  const { accountContext } = useAccountContext();

  const userName = accountContext?.currentUser?.name ?? '';
  const onNotifyChange = (eventData: OnNotifyChangeEventData) =>
    setState((prevState) => ({
      ...prevState,
      notifyTeam: eventData.teamMemberNotifySelected,
      stepNames: eventData.teamMemberNotifySelected
        ? [selectTeamStep, emailTeamStep]
        : [selectTeamStep],
    }));

  const handleSaveHirerTeamMemberJobAccessAndNotify =
    async (): Promise<StepActionResult> => {
      const saveResult = await saveHirerTeamMemberJobAccessAndNotify();
      if (saveResult.success) {
        return { success: true };
      }
      const errorMessage = t('Sorry we couldnt save your changes...');

      return { success: false, error: { errorMessage } };
    };
  const handleOnClickGoNext = (): StepActionResult => ({ success: true });

  return (
    <Stepper onComplete={onComplete} stepNames={state.stepNames}>
      <Step
        onCancel={onCancel}
        onClickNextAsync={
          state.notifyTeam
            ? undefined
            : handleSaveHirerTeamMemberJobAccessAndNotify
        }
        onClickNext={state.notifyTeam ? handleOnClickGoNext : undefined}
        footerNotification={<AddTeamMembersCallout teamMembers={teamMembers} />}
      >
        <HirerTeamMemberJobAccessForm
          id="team-members"
          onNotifyChange={onNotifyChange}
        />
      </Step>
      <Step
        saveButtonLabel={t('Send')}
        onClickNextAsync={handleSaveHirerTeamMemberJobAccessAndNotify}
      >
        <TeamMemberEmail
          jobId={jobId}
          jobTitle={jobTitle}
          userName={`${userName}`}
        />
      </Step>
    </Stepper>
  );
};
